import { FC } from 'react';
import { ChevronRight, FileText, Plus, Trash2 } from 'lucide-react';
import type { TranscriptBreakdown } from '@teachur/http-client';
import { StackedList } from '@new/common/ui/StackedList/StackedList';
import { TranscriptStatusBadge } from '@new/features/transcripts/shared/components/StatusBadge';
import { SystemTranscriptBadge } from '@new/features/transcripts/shared/components/SystemTranscriptBadge';
import { Button } from '@new/common/ui/Button/Button';

interface EvaluationTranscriptsListProps {
  transcripts: TranscriptBreakdown[];
  onSelect?: (transcriptId: number) => void;
  onDelete?: (transcript: TranscriptBreakdown) => void;
  onAdd?: () => void;
}

export const EvaluationTranscriptsList: FC<EvaluationTranscriptsListProps> = ({
  transcripts,
  onSelect,
  onDelete,
  onAdd,
}) => {
  const rowContent = (t: TranscriptBreakdown) => (
    <>
      <StackedList.Icon className="rounded-full bg-(--neutral-white-NW900) text-(--neutral-black-NB600)">
        <FileText className="size-5" />
      </StackedList.Icon>
      <StackedList.Content>
        <StackedList.Label>{t.institution}</StackedList.Label>
        <SystemTranscriptBadge kind={t.systemKind} />
      </StackedList.Content>
    </>
  );

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-4">
        <h6 className="[font-size:var(--fs-p3)]! [font-family:var(--font-arvo)]! font-semibold!">
          Transcripts ({transcripts.length})
        </h6>
        {onAdd ? (
          <Button
            variant="primary"
            size="sm"
            leftIcon={<Plus className="size-4" />}
            onClick={onAdd}
            text="Add"
          />
        ) : null}
      </div>
      {transcripts.length === 0 ? (
        <p className="text-sm text-(--neutral-black-NB600)">This student has no transcripts yet.</p>
      ) : (
        <StackedList>
          {transcripts.map((t) => (
            <StackedList.Item
              key={t.id}
              className={
                onSelect ? 'transition-colors hover:bg-(--neutral-white-NW900)' : undefined
              }
            >
              {onSelect ? (
                <button
                  type="button"
                  onClick={() => onSelect(t.id)}
                  className="flex min-w-0 flex-auto cursor-pointer items-center gap-x-4 text-left"
                >
                  {rowContent(t)}
                </button>
              ) : (
                rowContent(t)
              )}
              <StackedList.Action>
                <TranscriptStatusBadge status={t.status} />
                {onDelete ? (
                  <button
                    type="button"
                    aria-label={`Delete ${t.institution}`}
                    onClick={() => onDelete(t)}
                    className="cursor-pointer rounded p-1 text-(--neutral-black-NB600) hover:text-(--danger-D700)"
                  >
                    <Trash2 className="size-4" />
                  </button>
                ) : null}
                {onSelect ? <ChevronRight className="size-4 text-(--neutral-black-NB500)" /> : null}
              </StackedList.Action>
            </StackedList.Item>
          ))}
        </StackedList>
      )}
    </section>
  );
};
